import React, { useEffect, useRef, useState } from "react";
import ct from "class-types.macro";
import { cls } from "@wss/cls";
import Step, { ContactStep } from "./Step";
import { DefaultFormData } from "./NextSteps/ContactCustomerService/GeneralContactForm";

export type StepAndAnswer = {
  step: number;
  answers: Answer[];
};

export type Answer = {
  questionId: number;
  answerId: number;
};

interface Props {
  contactId: number;
  initialStep: ContactStep;
  defaultFormData: DefaultFormData;
  classNames?: string;
}

export default function SupportAsk({
  contactId,
  initialStep,
  defaultFormData,
  classNames = "",
}: Props) {
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState(initialStep);
  const getInTouchDiv = useRef<HTMLDivElement>(null);
  const [stepAndAnswers, setStepAndAnswers] = useState<StepAndAnswer[]>([]);
  const askAQuestionButton = useRef<HTMLButtonElement>(null);
  const cancelButton = useRef<HTMLButtonElement>(null);
  const askAQuestionClicked = useRef<boolean>(false);

  useEffect(() => {
    if (document.location.hash === "#askAQuestion") {
      setIsFormOpen(true);

      if (getInTouchDiv && getInTouchDiv.current) {
        getInTouchDiv.current.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, []);

  // Accessibility: Forces focus to the question when the form opens
  useEffect(() => {
    if (isFormOpen) {
      cancelButton.current?.focus();
    } else if (askAQuestionClicked.current) {
      askAQuestionButton.current?.focus();
    }
  }, [isFormOpen]);

  const handleToggleFormOpenClick = (isOpen?: boolean) => {
    setIsFormOpen(isOpen ?? !isFormOpen);
    setCurrentStep(initialStep);
    setStepAndAnswers([]);
  };

  const getClassNames = () => {
    let result = "support-help support-section clears";

    if (isFormOpen) {
      if (hasSubmitted) {
        result += " support-submitted";
      } else {
        result += " support-open";
      }
    }
    return result;
  };

  return (
    <div className={cls(getClassNames(), classNames)} ref={getInTouchDiv}>
      <div className="support-ask">
        <img
          className="support-help-icon"
          src="//cdnimg.webstaurantstore.com/uploads/design/2017/3/support-basic-icon.png"
          alt="Need to email Customer Solutions?"
        />
        <p className="header-2 text-base xs:text-xl lt:text-xl md:text-2xl lg:text-2xl pr-4">
          Need to email Customer Solutions?
        </p>
        <p className={ct("text-sm", "leading-tight", "pr-4", "text-gray-800")}>
          Let&#39;s find solutions to your questions!
        </p>
        <button
          type="button"
          className="
            btn
            btn-small
            xl:btn-medium
            btn-info
            btn-faq"
          onClick={() => {
            handleToggleFormOpenClick(true);
            setHasSubmitted(false);
            askAQuestionClicked.current = true;
          }}
          ref={askAQuestionButton}
          aria-label="Ask A Question, launches a multi-step form"
        >
          {hasSubmitted ? "Ask another question" : "Ask a Question"}
        </button>
        <button
          type="button"
          className="
            icon-cancel-support
            text-xs
            py-1
            px-2
            lt:text-xs
            lt:py-1
            lt:px-2
            md:text-sm
            md:px-4
            lg:text-sm
            lg:py-2
            lg:px-4
            rounded
            bg-transparent
            text-gray-900
            hover:bg-gray-200
            hover:no-underline
            hover:bg-position-y-15"
          onClick={() => {
            handleToggleFormOpenClick(false);
          }}
          ref={cancelButton}
        >
          Cancel
        </button>
      </div>
      <div className="support-form">
        <Step
          contactId={contactId}
          currentStep={currentStep}
          defaultFormData={defaultFormData}
          setHasSubmitted={setHasSubmitted}
          setCurrentStep={setCurrentStep}
          stepAndAnswers={stepAndAnswers}
          setStepAndAnswers={setStepAndAnswers}
        />
      </div>
    </div>
  );
}
